"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { Logo } from "@/components/ui/Logo";
import { cn } from "@/lib/utils";
import {
  Bars3Icon,
  XMarkIcon,
  MapPinIcon,
  UserCircleIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import HeaderLocationDisplay from "@/components/layout/HeaderLocationDisplay";
import AppearanceSettings from "@/components/profile/AppearanceSettings";
import { useAuth } from "@/contexts/AuthContext";
import { useSellerListingAttentionContext } from "@/contexts/SellerListingAttentionContext";
import { useAdminProductsAttentionContext } from "@/contexts/AdminProductsAttentionContext";
import { AdminProductsAttentionNavBadge } from "@/components/nav/AdminProductsAttentionNavBadge";
import { SellerMyItemsAttentionNavIndicator } from "@/components/nav/SellerMyItemsAttentionNavIndicator";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useReferralMe } from "@/lib/hooks/useReferralMe";
import NotificationBell from "@/components/notifications/NotificationBell";
import NotificationSetupPassiveChip from "@/components/notifications/NotificationSetupPassiveChip";
import NotificationDropdownPanel from "@/components/notifications/NotificationDropdownPanel";

const navigation = [
  { name: "Home", href: "/" },
  { name: "Marketplace", href: "/market" },
  { name: "Sell", href: "/sell", requireAuth: true },
  { name: "How It Works", href: "/#how-it-works" },
  { name: "Testimonials", href: "/#testimonials" },
  { name: "FAQs", href: "/#faqs" },
];

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, isLoading: isAuthLoading, logout } = useAuth();
  const { isPartner: referralNavPartner } = useReferralMe(
    isAuthenticated && !isAuthLoading,
  );
  const showReferralHubNav =
    user?.partner_type === "market_partner" || referralNavPartner;
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationWrapRef = useRef<HTMLDivElement>(null);
  const desktopNotificationsPanelId = "desktop-notification-dropdown";
  const { canInstall, isInstalled, install } = usePWAInstall();
  const {
    needsAttention: myItemsNeedsAttention,
    itemsNeedingAttentionCount: myItemsAttentionCount,
  } = useSellerListingAttentionContext();
  const { count: adminProductsAttentionCount } =
    useAdminProductsAttentionContext();

  // Track auth timeout to handle failed auth initialization
  const [authTimeout, setAuthTimeout] = useState(false);

  useEffect(() => {
    // Set a timeout to consider auth failed after 5 seconds
    const timer = setTimeout(() => {
      if (isAuthLoading) {
        console.warn(
          "Authentication is taking too long, showing login/signup options",
        );
        setAuthTimeout(true);
      }
    }, 5000);

    // Clear timeout if auth loads successfully
    if (!isAuthLoading) {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isAuthLoading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    if (!isNotificationsOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!notificationWrapRef.current) return;
      if (!notificationWrapRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isNotificationsOpen]);

  const handleLogout = async () => {
    setIsNotificationsOpen(false);
    await logout();
    setUserMenuOpen(false);
  };

  const handleNavigation = () => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }

    if (userMenuOpen) {
      setUserMenuOpen(false);
    }

    if (isNotificationsOpen) {
      setIsNotificationsOpen(false);
    }
  };

  const handleSectionNavigation = (
    sectionId: string,
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    // If we're already on the homepage, prevent default link behavior and scroll to the section
    if ((pathname ?? "/") === "/") {
      event.preventDefault();

      // Try finding with the ID with or without the hash
      let section = document.getElementById(sectionId);

      // If not found, try with hash
      if (!section) {
        section = document.getElementById(`#${sectionId}`);
      }

      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
        handleNavigation(); // Close any open menus
      } else {
        console.log(`Section with ID ${sectionId} or #${sectionId} not found`);
        // Fallback to App Router navigation if element not found
        router.push(`/#${sectionId}`);
        handleNavigation();
      }
    }
    // Otherwise, let the default link navigation happen (to /#section-id)
  };

  // Determine if a nav item should use smooth scrolling
  const shouldUseSmoothScroll = (itemName: string) => {
    return ["How It Works", "Testimonials", "FAQs"].includes(itemName);
  };

  // Handle hash navigation when the component mounts
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash.replace("#", "");
      if (hash) {
        // Try finding with the ID with or without the hash
        let section = document.getElementById(hash);

        // If not found, try with hash
        if (!section) {
          section = document.getElementById(`#${hash}`);
        }

        if (section) {
          setTimeout(() => {
            section?.scrollIntoView({ behavior: "smooth" });
          }, 300);
        }
      }
    };

    // Call on mount and when URL hash changes
    handleHashNavigation();
    window.addEventListener("hashchange", handleHashNavigation);

    return () => {
      window.removeEventListener("hashchange", handleHashNavigation);
    };
  }, []);

  // Determine if we should show authenticated content
  const showAuthenticatedContent = isAuthenticated && !isAuthLoading;
  // Show login/signup if not authenticated OR if auth is taking too long
  const showLoginSignup = !isAuthenticated && (!isAuthLoading || authTimeout);

  // Extract section ID from href
  const getSectionId = (href: string) => {
    if (href.startsWith("/#")) {
      return href.substring(2); // Remove '/#'
    }
    return "";
  };

  return (
    <header className="mt-20 bg-surface shadow-sm hidden lg:block">
      <nav
        className="fixed top-0 left-0 right-0 z-[10010] m-4 sio-glass rounded-sio-lg mx-auto flex max-w-7xl items-center justify-between p-4 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1 items-center">
          <Link href="/" className="-m-1.5 p-1.5 flex items-center">
            <span className="sr-only">Sell It Off</span>
            <Logo width={40} height={40} className="mr-2" />
            <span className="text-2xl font-bold text-primary">Sell It Off</span>
          </Link>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-foreground"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div className="hidden lg:flex lg:gap-x-8">
          {navigation.map((item) =>
            shouldUseSmoothScroll(item.name) ? (
              <Link
                key={item.name}
                href={
                  item.requireAuth && !isAuthenticated
                    ? `/login?redirectTo=${item.href}`
                    : item.href
                }
                className="text-sm font-semibold leading-6 text-foreground hover:text-primary"
                onClick={(e) =>
                  handleSectionNavigation(getSectionId(item.href), e)
                }
              >
                {item.name}
              </Link>
            ) : (
              <Link
                key={item.name}
                href={
                  item.requireAuth && !isAuthenticated
                    ? `/login?redirectTo=${item.href}`
                    : item.href
                }
                className="text-sm font-semibold leading-6 text-foreground hover:text-primary"
                onClick={() => handleNavigation()}
              >
                {item.name}
              </Link>
            ),
          )}
        </div>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:gap-x-4">
          <div className="flex items-center mr-4 text-sm text-text-light">
            <MapPinIcon className="h-4 w-4 mr-1 text-primary" />
            <HeaderLocationDisplay />
          </div>

          {isAuthLoading && !authTimeout ? (
            // Show loading state while auth is initializing (but not timed out)
            <div className="flex items-center">
              <div className="h-5 w-5 border-t-2 border-primary rounded-full animate-spin mr-2"></div>
              <span className="text-sm text-text-light">Loading...</span>
            </div>
          ) : showAuthenticatedContent ? (
            // Show user menu if authenticated
            <div className="flex items-center gap-2">
              <NotificationSetupPassiveChip />
              <div className="relative z-[10020]" ref={notificationWrapRef}>
                <NotificationBell
                  mode="dropdown"
                  ariaExpanded={isNotificationsOpen}
                  ariaControls={desktopNotificationsPanelId}
                  onClick={() => {
                    setIsNotificationsOpen((open) => !open);
                    setUserMenuOpen(false);
                  }}
                />
                {isNotificationsOpen ? (
                  <NotificationDropdownPanel
                    panelId={desktopNotificationsPanelId}
                    userId={user?.id}
                    placement="anchored-right"
                    onClose={() => setIsNotificationsOpen(false)}
                  />
                ) : null}
              </div>
              <div className="relative" ref={userMenuRef}>
                <div>
                  <button
                    type="button"
                    className="flex items-center rounded-md bg-surface/0 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    id="user-menu-button"
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                    onClick={() => {
                      setUserMenuOpen(!userMenuOpen);
                      setIsNotificationsOpen(false);
                    }}
                  >
                    <span className="sr-only">Open user menu</span>
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-secondary mr-2">
                        {user?.avatar_url ? (
                          <Image
                            src={user?.avatar_url}
                            alt="Profile"
                            width={8}
                            height={8}
                            className="w-full h-full object-cover"
                            // onError={() => setAvatarUrl(undefined)}
                            unoptimized={process.env.NODE_ENV === "development"}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-tertiary">
                            <UserCircleIcon className="h-8 w-8 text-text-light mr-2" />
                          </div>
                        )}
                      </div>
                      <span className="text-sm font-medium text-foreground mr-2">
                        {user?.name?.slice(0, 11) + "..."}
                      </span>
                      <ChevronDownIcon className="h-6 w-6 text-text-light mr-2" />
                    </div>
                  </button>
                </div>

                {userMenuOpen && (
                  <div
                    className="absolute right-0 z-10 mt-2 w-64 origin-top-right overflow-hidden rounded-md bg-surface py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="user-menu-button"
                    tabIndex={-1}
                  >
                    {user?.isAdmin && (
                      <Link
                        href="/admin"
                        className="block px-4 py-2 text-sm text-foreground hover:bg-tertiary"
                        role="menuitem"
                        tabIndex={-1}
                        id="user-menu-item-admin"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Admin Dashboard
                      </Link>
                    )}
                    <Link
                      href="/profile"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-tertiary"
                      role="menuitem"
                      tabIndex={-1}
                      id="user-menu-item-0"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Your Profile
                    </Link>
                    <Link
                      href="/notifications"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-tertiary"
                      role="menuitem"
                      tabIndex={-1}
                      id="user-menu-notifications"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Notifications
                    </Link>
                    <Link
                      href="/my-items"
                      className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-foreground hover:bg-tertiary"
                      role="menuitem"
                      tabIndex={-1}
                      id="user-menu-item-1"
                      onClick={() => setUserMenuOpen(false)}
                      aria-label={
                        myItemsNeedsAttention
                          ? `My Items, ${myItemsAttentionCount} need your attention`
                          : "My Items"
                      }
                    >
                      <span>My Items</span>
                      <SellerMyItemsAttentionNavIndicator
                        show={myItemsNeedsAttention}
                        count={myItemsAttentionCount}
                      />
                    </Link>
                    <Link
                      href="/request-item"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-tertiary"
                      role="menuitem"
                      tabIndex={-1}
                      id="user-menu-item-request-item"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Request an item
                    </Link>
                    <Link
                      href="/my-requests"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-tertiary"
                      role="menuitem"
                      tabIndex={-1}
                      id="user-menu-item-my-requests"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      My Requests
                    </Link>
                    <Link
                      href="/my-purchases"
                      className="block px-4 py-2 text-sm text-foreground hover:bg-tertiary"
                      role="menuitem"
                      tabIndex={-1}
                      id="user-menu-item-3"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      My Purchases
                    </Link>
                    {showReferralHubNav && (
                      <Link
                        href="/refer"
                        className="block px-4 py-2 text-sm text-foreground hover:bg-tertiary"
                        role="menuitem"
                        tabIndex={-1}
                        id="user-menu-item-refer"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Referral Hub
                      </Link>
                    )}
                    {canInstall && !isInstalled && (
                      <button
                        className="block w-full text-left px-4 py-2 text-sm text-foreground hover:bg-tertiary flex items-center"
                        role="menuitem"
                        tabIndex={-1}
                        id="user-menu-item-install"
                        onClick={async () => {
                          try {
                            await install();
                            setUserMenuOpen(false);
                          } catch (_error) {
                            // Silently handle errors
                            setUserMenuOpen(false);
                          }
                        }}
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        Install App
                      </button>
                    )}
                    <div className="border-t border-border">
                      <AppearanceSettings
                        variant="sidebar"
                        className="px-3 py-3"
                      />
                    </div>
                    <button
                      className="block w-full border-t border-border px-4 py-2 text-left text-sm text-foreground hover:bg-tertiary"
                      role="menuitem"
                      tabIndex={-1}
                      id="user-menu-item-4"
                      onClick={handleLogout}
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : showLoginSignup ? (
            // Show login/signup buttons if not authenticated
            <>
              <Link
                href="/login"
                className="rounded-md border border-border px-4 py-2 text-sm font-semibold leading-6 text-foreground hover:border-border-strong"
              >
                Log in
              </Link>
              <Link
                href="/signup"
                className="rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold leading-6 text-white hover:bg-primary-600"
              >
                Sign up
              </Link>
            </>
          ) : null}
        </div>
      </nav>

      {/* Mobile menu, show/hide based on menu open state */}
      <div
        className={cn(
          "lg:hidden",
          mobileMenuOpen ? "fixed inset-0 z-50" : "hidden",
        )}
      >
        {/* Background backdrop */}
        <div className="fixed inset-0 bg-black/20" aria-hidden="true" />

        {/* Menu panel */}
        <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-surface px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-foreground/10">
          <div className="flex items-center justify-between">
            <Link href="/" className="-m-1.5 p-1.5 flex items-center">
              <span className="sr-only">Sell It Off</span>
              <Logo width={32} height={32} className="mr-2" />
              <span className="text-xl font-bold text-primary">
                Sell It Off
              </span>
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-foreground"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-border">
              <div className="py-3 flex items-center">
                <MapPinIcon className="h-4 w-4 mr-1 text-primary" />
                <HeaderLocationDisplay
                  className="text-sm text-text-light"
                  loadingClassName="text-sm text-text-light"
                  includeLagosSuffix
                />
              </div>

              {isAuthLoading && !authTimeout ? (
                <div className="py-3 flex items-center">
                  <div className="h-4 w-4 border-t-2 border-primary rounded-full animate-spin mr-2"></div>
                  <span className="text-sm text-text-light">
                    Loading user data...
                  </span>
                </div>
              ) : (
                showAuthenticatedContent && (
                  <div className="py-3 flex items-center">
                    <UserCircleIcon className="h-5 w-5 mr-2 text-primary" />
                    <span className="text-sm font-medium text-foreground">
                      {user?.name}
                    </span>
                  </div>
                )
              )}

              <div className="space-y-2 py-6">
                {navigation.map((item) =>
                  shouldUseSmoothScroll(item.name) ? (
                    <Link
                      key={item.name}
                      href={
                        item.requireAuth && !isAuthenticated
                          ? `/login?redirectTo=${item.href}`
                          : item.href
                      }
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                      onClick={(e) =>
                        handleSectionNavigation(getSectionId(item.href), e)
                      }
                    >
                      {item.name}
                    </Link>
                  ) : (
                    <Link
                      key={item.name}
                      href={
                        item.requireAuth && !isAuthenticated
                          ? `/login?redirectTo=${item.href}`
                          : item.href
                      }
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                      onClick={() => handleNavigation()}
                    >
                      {item.name}
                    </Link>
                  ),
                )}
              </div>

              <div className="py-4">
                <AppearanceSettings variant="sidebar" className="px-0" />
              </div>

              {showAuthenticatedContent ? (
                <div className="py-6 space-y-2">
                  {user?.isAdmin && (
                    <Link
                      href="/admin"
                      className="-mx-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                      onClick={() => setMobileMenuOpen(false)}
                      aria-label={
                        adminProductsAttentionCount > 0
                          ? `Admin Dashboard, ${adminProductsAttentionCount} products need review`
                          : "Admin Dashboard"
                      }
                    >
                      <span>Admin Dashboard</span>
                      <AdminProductsAttentionNavBadge
                        count={adminProductsAttentionCount}
                      />
                    </Link>
                  )}
                  <Link
                    href="/profile"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Your Profile
                  </Link>
                  <Link
                    href="/my-items"
                    className="-mx-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-label={
                      myItemsNeedsAttention
                        ? `My Items, ${myItemsAttentionCount} need your attention`
                        : "My Items"
                    }
                  >
                    <span>My Items</span>
                    <SellerMyItemsAttentionNavIndicator
                      show={myItemsNeedsAttention}
                      count={myItemsAttentionCount}
                    />
                  </Link>
                  <Link
                    href="/request-item"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Request an item
                  </Link>
                  <Link
                    href="/my-requests"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Requests
                  </Link>
                  <Link
                    href="/my-purchases"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Purchases
                  </Link>
                  {showReferralHubNav && (
                    <Link
                      href="/refer"
                      className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Referral Hub
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="-mx-3 block w-full text-left rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                  >
                    Sign out
                  </button>
                </div>
              ) : showLoginSignup ? (
                <div className="py-6 space-y-2">
                  <Link
                    href="/login"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-foreground hover:bg-secondary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Log in
                  </Link>
                  <Link
                    href="/signup"
                    className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 bg-primary-500 text-white hover:bg-primary-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign up
                  </Link>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
